import React, { useState, useEffect, useRef } from "react"
import _get from "lodash/get"
import { useDispatch, useSelector } from "react-redux"

import { getConfig } from "@/constants/config"
import { getShortenedUrl, sanitizeInnerHtml, resetUrl } from "@/utils/helper"
import closeIcon from "@/public/icons/Close Icon.svg"
import {
  setSearchCount,
  setSearchFilter,
  searchState,
} from "@/store/features/searchSlice"
import dynamic from "next/dynamic"
const PopularSearch = dynamic(() =>
  import("@/components/Default/DynamicSearch/v1/PopularSearch")
)
const NoResultLinks = dynamic(() =>
  import("@/components/Default/DynamicSearch/v1/NoResultLinks")
)

import SearchProgressive from "@/components/Search/v1/SearchProgressive"
import searchWrapperStyle from "@/components/Search/v1/SearchView/index.module.scss"

const SearchComponentView = props => {
  const {
    data,
    brand = "",
    loadPopularSearch,
    activeTabVal,
    tabList,
    searchKeyword,
    staticTexts,
    totalResults,
    onSearch,
    loading,
    handleSearchEnter,
    searchResponse,
  } = props
  const dispatch = useDispatch()
  const searchRef = useRef(null)
  const newState = useSelector(searchState)
  const {
    noResultPopularTopicsHeading = "",
    noResultPopularLinks = [],
    noResultOurSuggestionsHeading = "",
    noResultOurSuggestionsSubcopy = "",
  } = data

  const [activeTab, setActiveTab] = useState(
    activeTabVal ? (activeTabVal < 0 ? 0 : activeTabVal) : 0
  )
  const [homePagePath, setHomePagePath] = useState("")
  const [searchPlaceholder, setSearchplaceholder] = useState(brand)

  const [oldTotalResults, setOldTotalResults] = useState(totalResults)
  let isSpellTagged = false
  let resultsText = ""
  let keepSearching = ""
  const urlParams = new URL(window.location.href)

  let keyword = urlParams?.searchParams?.get("keyword")
  try {
    keyword = urlParams?.searchParams?.get("keyword")
      ? decodeURIComponent(urlParams?.searchParams?.get("keyword"))
      : ""
  } catch (error) {
    console.error("URIError: malformed URI sequence")
  }
  let currTab = activeTab ? Number(activeTab) : 0 // reqired for ADA focus

  const setCount = () => {
    const counts = {}
    tabList?.forEach(tab => {
      counts[tab?.id] = {
        count: tab?.count,
      }
      if (searchKeyword !== newState?.searchDetails?.keyword) {
        dispatch(setSearchFilter({ queries: [], key: tab?.id }))
      }
    })
    dispatch(setSearchCount(counts))
  }

  useEffect(() => {
    // Will trigger only when tab keyword is available in url.
    if (activeTabVal != undefined) {
      setActiveTab(activeTabVal)
    }
  }, [activeTabVal])

  useEffect(() => {
    getConfig()?.then(config => {
      const searchBrandPlaceholder =
        config?.apiEndPointData?.searchBrandPlaceholder ?? brand
      if (searchBrandPlaceholder?.length > 0) {
        setSearchplaceholder(searchBrandPlaceholder)
      }
      getShortenedUrl(config?.general?.homepageUrl ?? "/")?.then(res =>
        setHomePagePath(res)
      )
    })

    setCount()
  }, [])
  useEffect(() => {
    if (loading) {
      setCount()
      setActiveTab(0)
    }
  }, [searchKeyword, loading])
  useEffect(() => {
    if (activeTab < 0) {
      setActiveTab(0)
    }
    if (tabList?.length <= (activeTab ? Number(activeTab) : 0)) {
      setActiveTab(0)
    }
  })

  if (
    searchResponse?.data?.fusion?.tagger &&
    searchResponse?.data?.fusion?.tagger[0] &&
    searchResponse?.data?.fusion?.tagger[0]?.tag?.type === "spell"
  ) {
    isSpellTagged = true
    keepSearching = searchResponse?.data?.fusion?.tagger[0]?.surfaceForm
    resultsText = `${staticTexts?.showing} ${totalResults?.toLocaleString()} ${
      staticTexts?.results
    } ${staticTexts?.for} "${
      searchResponse?.data?.fusion?.tagger[0]?.outputForms[0]
    }"`
  } else if (
    searchResponse?.data?.fusion?.autoCorrectQuery &&
    searchResponse?.data?.fusion?.org_q
  ) {
    isSpellTagged = true
    keepSearching = searchResponse?.data?.fusion?.org_q
    resultsText = `${staticTexts?.showing} ${totalResults?.toLocaleString()} ${
      staticTexts?.results
    } ${staticTexts?.for} "${
      searchKeyword?.length < 3
        ? keyword
        : searchResponse?.data?.fusion?.autoCorrectQuery
    }"`
  } else {
    resultsText = `${staticTexts?.showing} ${totalResults?.toLocaleString()} ${
      staticTexts?.results
    } ${staticTexts?.for} "${
      searchKeyword?.length < 3 ? keyword : searchKeyword
    }"`
  }

  const changeTab = tab => {
    const query = window?.location?.search
    const currentTab = tabList[tab]
    if (sessionStorage?.getItem("SRPTabDetails")) {
      const srpTabDetails = JSON.parse(sessionStorage?.getItem("SRPTabDetails"))
      const url = srpTabDetails[currentTab?.id]
      if (url)
        history?.replaceState(window?.location?.pathname, document?.title, url)
      else resetUrl()
    } else if (query) resetUrl()

    if (
      newState?.searchDetails?.keyword &&
      newState?.searchDetails?.keyword !== searchKeyword
    ) {
      setCount()
    }
    if (searchKeyword === "") {
      const urlParams = new URLSearchParams(window?.location?.search)
      const keyword = urlParams?.get("keyword")
      onSearch(keyword)
    }
    setActiveTab(tab)
  }
  const handleClose = (value = 0) => {
    setOldTotalResults(value)
    onSearch("", false)
    const input = document.getElementById("search_close_button")
    input?.focus()
  }

  const handleKeyPress = e => {
    if (e.keyCode === 37 || e.keyCode === 39) {
      if (e.keyCode === 37) {
        currTab = currTab - 1
      } else if (e.keyCode === 39) {
        currTab = currTab + 1
      }
      if (currTab < 0) currTab = tabList?.length - 1
      if (currTab >= tabList?.length) currTab = 0
      document.getElementById(`${tabList[currTab]?.title}-tab`)?.focus()
    }
  }

  const searchBoxEventInfo = type => {
    if (searchRef?.current?.value) {
      return JSON.stringify({
        eventAction: "search:search box:" + type,
        eventName: "search:search box:" + type,
        eventType: "navigation",
        eventMsg: "n/a",
        eventStatus: "n/a",
        internalLinkName: type,
        internalLinkPosition: "search",
        internalLinkType: `search:${keyword}`,
        internalLinkZoneName: "search:search box",
        internalLinkURL: "n/a",
        clickInternalLinks: "true",
        searchTerm: keyword,
        searchResult: oldTotalResults,
      })
    }
  }

  const breadcrumbEventInfo = type => {
    const homeBreadcrumb = type?.toLowerCase() === "home"
    return JSON.stringify({
      eventAction: "search:breadcrumb",
      eventName: "search:breadcrumb",
      eventType: "navigation",
      eventMsg: "n/a",
      eventStatus: "n/a",
      internalLinkName: homeBreadcrumb
        ? `${staticTexts?.home}`
        : `${staticTexts?.home} > ${staticTexts?.searchResults}`,
      internalLinkPosition: "search",
      internalLinkType: "search:breadcrumb",
      internalLinkZoneName: "search:search box",
      internalLinkURL: homeBreadcrumb
        ? `${homePagePath}`
        : `${window?.location?.href}`,
      clickInternalLinks: "true",
    })
  }

  return (
    <div>
      <div
        className={`${searchWrapperStyle?.searchViewStyleWrapper} Plp-Search`}
      >
        <div className="search-result" id="searchWrapperId">
          <div className="container-fluid">
            <div className="product-listing__row no-gutters">
              <div
                className={`product-listing__col search-result__breadcrumb-header ${
                  totalResults === 0 ? " --no-results" : ""
                }`}
              >
                <div className="search-result__breadcrumb kf-react-plp-container">
                  <nav aria-label={staticTexts?.ariaLabel?.breadcrumb}>
                    <ol role="list">
                      <li role="listitem">
                        <a
                          aria-label={`${staticTexts?.home} ${staticTexts?.ariaLabel?.page}`}
                          className="gbh-data-layer"
                          data-gbh-data-layer={breadcrumbEventInfo(
                            staticTexts?.home?.trim()
                          )}
                          href={homePagePath}
                        >
                          {staticTexts?.home}
                        </a>
                        {" / "}
                      </li>
                      <li role="listitem">
                        <a
                          aria-label={
                            staticTexts?.searchResults +
                            " " +
                            staticTexts?.ariaLabel?.page
                          }
                          className="gbh-data-layer"
                          aria-current={staticTexts?.ariaLabel?.page}
                          data-gbh-data-layer={breadcrumbEventInfo(
                            staticTexts?.searchResults?.trim()
                          )}
                          href="#"
                        >
                          {staticTexts?.searchResults}
                        </a>
                      </li>
                    </ol>
                  </nav>
                </div>
                <div
                  className={`search-result__header ${
                    loading || !totalResults
                      ? "search-result--no-background"
                      : ""
                  }`}
                >
                  <div className="kf-react-plp-container">
                    <div className="search-result__search-header">
                      <div className="search-result__description">
                        <div
                          role="alert"
                          aria-live="polite"
                          className={`search-result__showing ${
                            totalResults === 0 ? " --no-results" : ""
                          }`}
                        >
                          {loading ? (
                            `${staticTexts?.loading} ${staticTexts?.results}`
                          ) : totalResults === 0 ? (
                            staticTexts?.noResults
                          ) : (
                            <h1>{resultsText}</h1>
                          )}
                        </div>
                        {!loading &&
                          isSpellTagged === true &&
                          searchKeyword?.length > 2 && (
                            <span className="search-result__instead">
                              {staticTexts?.searchInstead}{" "}
                              <span
                                onClick={() =>
                                  onSearch(keepSearching, true, true)
                                }
                              >
                                {keepSearching}
                              </span>
                            </span>
                          )}
                      </div>
                      <div className="search-result__search-field">
                        <input
                          ref={searchRef}
                          type="text"
                          id="search_close_button"
                          className="search-result__search-control"
                          value={searchKeyword}
                          onChange={e => onSearch(e?.target?.value)}
                          onKeyDown={e =>
                            (e.key === "Enter" || e.keyCode === 13) &&
                            handleSearchEnter()
                          }
                          autoFocus
                          placeholder={`${staticTexts?.searchBrand} ${searchPlaceholder}`}
                        />
                        <a
                          className={`search-result__close-button${
                            keyword ? " gbh-data-layer" : ""
                          }`}
                          role="button"
                          tabIndex="0"
                          aria-label={staticTexts?.ariaLabel?.close}
                          data-gbh-data-layer={searchBoxEventInfo("close")}
                          onClick={() => handleClose(totalResults)}
                        >
                          <img src={closeIcon?.src} alt="close-icon" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {loading ? (
              <SearchProgressive />
            ) : (
              <div className="row no-gutters">
                {!loading &&
                  (totalResults > 0 ? (
                    <div className="col-12">
                      <div className="search-result__tab-header">
                        <div
                          className="container search-result__tab-buttons kf-react-container"
                          role="tablist"
                        >
                          <div className="d-flex">
                            {tabList?.map((tab, i) => {
                              return (
                                <button
                                  key={i}
                                  id={`${tab?.title}-tab`}
                                  aria-label={staticTexts?.ariaLabel?.tabs(
                                    newState?.searchDetails[tab?.id]?.count,
                                    tab?.title
                                  )}
                                  aria-selected={
                                    (activeTab ? Number(activeTab) : 0) === i
                                      ? true
                                      : false
                                  }
                                  role="tab"
                                  data-gbh-data-layer={`{"eventAction":"search results:hero:inner tabs","eventName":"search results:hero:inner tabs","eventType":"navigation","eventMsg":"n/a","eventStatus":"n/a","internalLinkName":"${tab?.title}","internalLinkPosition":"search","internalLinkType":"search results:navigation","internalLinkZoneName":"search results:hero","internalLinkURL":"n/a","clickInternalLinks":"true"}`}
                                  className={`search-result__tab-button gbh-data-layer ${
                                    (activeTab ? Number(activeTab) : 0) === i
                                      ? "--active"
                                      : ""
                                  }`}
                                  onClick={() => changeTab(i)}
                                  onKeyDown={e => handleKeyPress(e)}
                                  tabIndex={
                                    (activeTab ? Number(activeTab) : 0) === i
                                      ? 0
                                      : -1
                                  }
                                >
                                  <span role="heading" aria-level="2">{`${
                                    tab?.title
                                  } (${tab?.count?.toLocaleString()})`}</span>
                                </button>
                              )
                            })}
                          </div>
                        </div>
                      </div>
                      <div className="search-result__tab-content">
                        {_get(
                          tabList[activeTab ? Number(activeTab) : 0],
                          "content",
                          ""
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="col-12">
                      <div className="search-result__no-result">
                        <div className="row">
                          <div className="col-lg-4 col-sm-12 search-result__no-result-column">
                            <h2 className="search-result__suggestions-title wow animate__slideInLeft">
                              {noResultOurSuggestionsHeading}
                            </h2>
                            <div className="search-result__suggestions-list wow animate__fadeInUp">
                              {sanitizeInnerHtml(noResultOurSuggestionsSubcopy)}
                            </div>
                          </div>
                          <div className="col-lg-4 col-sm-12 search-result__no-result-column">
                            <PopularSearch
                              staticTexts={staticTexts}
                              loadPopularSearch={loadPopularSearch}
                              brand={brand}
                              updateFromResult={onSearch}
                              staticData={data}
                            />
                          </div>
                          <div className="col-lg-4 col-sm-12 search-result__no-result-column">
                            <NoResultLinks
                              data={noResultPopularLinks}
                              link
                              heading={noResultPopularTopicsHeading}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SearchComponentView
